import { Button, Divider, Stack } from "@mui/material";
import { DialogWindow } from "./DialogWindow";
import { useEffect, useState } from "react";
import { Confirmation } from "./Confirmation";
import { Spinner } from "UI/Spinner";
import { AddNewSenderDialog } from "./AddNewSenderDialog";

const compareArrays = (arr1, arr2) => {
  if (arr1.length !== arr2.length) return false;
  return (
    arr1.every((item) => arr2.includes(item)) &&
    arr2.every((item) => arr1.includes(item))
  );
};

export const SenderAssignmentDialog = ({
  handleClose,
  setError,
  senders,
  updateSendersInButtonLocations,
  currentButton,
  originator,
  selectNewlyAddedSender,
}) => {
  const [previousSenders, setPreviousSenders] = useState([]);
  const [selectedSenders, setSelectedSenders] = useState([]);
  const [confirmation, setConfirmation] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isAddNewSenderDialogOpen, setIsAddNewSenderDialogOpen] =
    useState(false);

  useEffect(() => {
    const buttonId = Object.keys(currentButton)[0];
    if (!buttonId) return;
    const currentButtonSenders = (currentButton[buttonId]?.senders ?? [])
      .map(({ id }) => id)
      .filter((id) => id !== originator?.id);
    setPreviousSenders(currentButtonSenders);
    setSelectedSenders(currentButtonSenders);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentButton]);

  const handleToggleSender = (senderId) => {
    if (selectedSenders.includes(senderId)) {
      setSelectedSenders((prev) => prev.filter((id) => id !== senderId));
    } else {
      setSelectedSenders((prev) => [...prev, senderId]);
    }
  };

  const isChanged = !compareArrays(selectedSenders, previousSenders);

  const confirmedClose = () => {
    if (!compareArrays(selectedSenders, previousSenders)) {
      setConfirmation({
        title: "You have unsaved changes",
        message: "Are you sure you want to close this dialog?",
        proceedFn: () => handleClose(),
        cancelFn: () => setConfirmation(null),
      });
      return;
    }
    handleClose();
  };

  const handleSave = async () => {
    if (!isChanged) return;

    try {
      const buttonId = Object.keys(currentButton)[0];
      if (!buttonId) return;
      setIsUpdating(true);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/button_senders`,
        {
          method: "POST",
          body: JSON.stringify({
            buttonId,
            senders: [...selectedSenders, originator.id],
            accessToken: originator?.dashboardAccessToken ?? "",
          }),
        }
      );

      const result = await response.json();
      if (result?.error || result?.message) {
        setError(result?.error || result?.message);
        setIsUpdating(false);
        return;
      }

      updateSendersInButtonLocations(buttonId, result);
      handleClose();
    } catch (e) {
      setError(e?.message ?? "Something went wrong");
    } finally {
      setIsUpdating(false);
    }
  };

  const practiceName =
    currentButton[Object.keys(currentButton)[0]]?.recipient?.name;

  return (
    <DialogWindow
      title={<div className="font-bold">Manage Button Senders</div>}
      content={
        <>
          <div className="flex text-left flex-col mt-4 gap-4 select-none">
            <div className="flex flex-row flex-wrap gap-x-2">
              Button from: <div className="text-black">{practiceName}</div>
            </div>
            <div className="flex-1 max-h-60 overflow-y-auto pr-4 divide-y-2">
              {!senders?.length && (
                <div className="flex flex-col items-stretch gap-4">
                  <Divider />
                  <div className="text-sm text-center text-gray-400 sm:text-md">
                    No senders in your Practice yet.
                  </div>
                  <Divider />
                </div>
              )}
              {senders
                ?.sort((a, b) => {
                  if (a.firstName < b.firstName) return -1;
                  if (a.firstName > b.firstName) return 1;
                  return 0;
                })
                ?.filter(({ id }) => id !== originator?.id)
                ?.map(({ id, firstName, lastName, jobTitle, phone, email }) => (
                  <div key={id} className="flex flex-row justify-start gap-2">
                    <label
                      htmlFor={`checkbox-${id}`}
                      className={`
                        flex flex-row justify-start items-center gap-2 py-2
                        text-sm text-gray-700 sm:text-md text-left cursor-pointer hover:text-blue-700
                      `}
                    >
                      <input
                        id={`checkbox-${id}`}
                        type="checkbox"
                        checked={selectedSenders.includes(id)}
                        onChange={(e) => handleToggleSender(id)}
                        className="cursor-pointer"
                      />
                      {[`${firstName} ${lastName}`, jobTitle, phone ?? email]
                        .filter(Boolean)
                        .join(", ")}
                    </label>
                  </div>
                ))}
            </div>
            <button
              onClick={() => setIsAddNewSenderDialogOpen(true)}
              className="text-blue-500 hover:text-blue-600 active:text-blue-800"
            >
              Add new sender
            </button>
            <Stack
              direction={"row"}
              justifyContent={"center"}
              alignItems={"center"}
              gap={1}
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={confirmedClose}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={!isChanged || isUpdating}
                onClick={handleSave}
              >
                Save
              </Button>
            </Stack>
          </div>
          {confirmation && <Confirmation {...confirmation} />}
          {isUpdating && <Spinner />}
          {isAddNewSenderDialogOpen && (
            <AddNewSenderDialog
              handleClose={() => setIsAddNewSenderDialogOpen(false)}
              currentButton={currentButton}
              originator={originator}
              setError={setError}
              addNewSender={selectNewlyAddedSender}
              setSelectedSenders={setSelectedSenders}
            />
          )}
        </>
      }
      handleClose={confirmedClose}
      withCloseButton
    />
  );
};
